/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import {
  createAsyncThunk,
  createSlice as _createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import {
  Accreditation,
  ClassUpdated as Class,
  Course,
  LearningPath,
  LinkedService,
  Quiz,
  Section,
  Series,
  StreamStep,
  Template,
  TextContent,
  TitleScreen,
  Video,
} from 'src/v3/core/types'
import axios, { pluckData } from 'src/utilities/http-client'

export let fetchTemplates = createAsyncThunk(
  'create/fetchTemplatesStatus',
  () => axios.get<Template[]>('/api/v2/create/templates').then(pluckData),
)

export let fetchSections = createAsyncThunk('create/fetchSectionsStatus', () =>
  axios.get<Section[]>('/api/v2/create/sections').then(pluckData),
)

export let fetchCourses = createAsyncThunk('create/fetchCoursesStatus', () =>
  axios.get<Course[]>('/api/v2/create/courses').then(pluckData),
)

export let fetchClasses = createAsyncThunk('create/fetchClassesStatus', () =>
  axios.get<Class[]>('/api/v2/create/classes').then(pluckData),
)

export let fetchAccreditations = createAsyncThunk(
  'create/fetchAccreditationsStatus',
  () =>
    axios.get<Accreditation[]>('/api/v2/create/accreditations').then(pluckData),
)

export let fetchTitleScreens = createAsyncThunk(
  'create/fetchTitleScreenStatus',
  (id: string) =>
    axios
      .get<TitleScreen>(`/api/v2/create/title-screen?ClassID=${id}`)
      .then(pluckData),
)

export let fetchVideos = createAsyncThunk('create/fetchVideosStatus', () =>
  axios.get<Video[]>('/api/v2/create/video').then(pluckData),
)

export let fetchTextContent = createAsyncThunk(
  'create/fetchTextContentStatus',
  (id: string) =>
    axios
      .get<TextContent[]>(`/api/v2/create/text-content?ClassID=${id}`)
      .then(pluckData),
)

export let fetchQuizzes = createAsyncThunk(
  'create/fetchQuizStatus',
  (id: string) =>
    axios.get<Quiz>(`/api/v2/create/quiz?ClassID=${id}`).then(pluckData),
)

export let fetchStoryboardSteps = createAsyncThunk(
  'create/fetchStoryboardStepsStatus',
  () => axios.get<StreamStep[]>('/api/v2/create/stream').then(pluckData),
)

export let fetchSeries = createAsyncThunk('create/fetchSeries', () =>
  axios.get<Series[]>(`/api/v2/create/series`).then(pluckData),
)

export let fetchLearningPaths = createAsyncThunk(
  'create/fetchLearningPaths',
  () =>
    axios.get<LearningPath[]>(`/api/v2/create/learning-paths`).then(pluckData),
)

export let fetchLinkedServices = createAsyncThunk(
  'linkedService/fetchLinkedServices',
  () =>
    axios
      .get<LinkedService[]>('/api/v2/create/linked-services')
      .then(pluckData),
)

type EntityStatus = string | 'unselected'
type CreateState = {
  activeLinkedService: EntityStatus
  activeSection: EntityStatus
  activeCourse: EntityStatus
  activeClass: EntityStatus
  templatesStatus: Status
  sectionsStatus: Status
  coursesStatus: Status
  classesStatus: Status
  accreditationsStatus: Status
  titleScreensStatus: Status
  videosStatus: Status
  textContentStatus: Status
  quizzesStatus: Status
  storyboardsStatus: Status
  templates: Template[]
  sections: Section[]
  courses: Course[]
  classes: Class[]
  accreditations: Accreditation[]
  titleScreens: Record<string, TitleScreen>
  videos: Video[]
  textContent: Record<string, TextContent[]>
  quizzes: Record<string, Quiz>
  storyboardSteps: StreamStep[]
  series: Series[]
  seriesStatus: Status
  learningPaths: LearningPath[]
  learningPathsStatus: Status
  linkedServicesStatus: Status
  linkedServices: LinkedService[]
}

export let initialState: CreateState = {
  activeLinkedService: 'unselected',
  activeSection: 'unselected',
  activeCourse: 'unselected',
  activeClass: 'unselected',
  templatesStatus: 'uninitialized',
  sectionsStatus: 'uninitialized',
  coursesStatus: 'uninitialized',
  classesStatus: 'uninitialized',
  accreditationsStatus: 'uninitialized',
  titleScreensStatus: 'uninitialized',
  videosStatus: 'uninitialized',
  textContentStatus: 'uninitialized',
  quizzesStatus: 'uninitialized',
  storyboardsStatus: 'uninitialized',
  templates: [],
  sections: [],
  courses: [],
  classes: [],
  accreditations: [],
  titleScreens: {},
  videos: [],
  textContent: {},
  quizzes: {},
  storyboardSteps: [],
  series: [],
  seriesStatus: 'uninitialized',
  learningPaths: [],
  learningPathsStatus: 'uninitialized',
  linkedServicesStatus: 'uninitialized',
  linkedServices: [],
}

export let createSlice = _createSlice({
  name: 'create',
  initialState,
  reducers: {
    activeLinkedServiceChanged: (
      state,
      action: PayloadAction<EntityStatus>,
    ) => {
      state.activeLinkedService = action.payload
    },
    activeSectionChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeSection = action.payload
    },
    activeCourseChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeCourse = action.payload
    },
    activeClassChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeClass = action.payload
    },
    sectionsChanged: (state, action: PayloadAction<Section[]>) => {
      state.sections = action.payload
    },
    titleScreensChanged: (state, action: PayloadAction<TitleScreen>) => {
      state.titleScreens[state.activeClass] = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.templatesStatus = 'pending'
      })
      .addCase(fetchTemplates.rejected, (state) => {
        state.templatesStatus = 'rejected'
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templatesStatus = 'fulfilled'
        state.templates = action.payload
      })
      .addCase(fetchSections.pending, (state) => {
        state.sectionsStatus = 'pending'
      })
      .addCase(fetchSections.rejected, (state) => {
        state.sectionsStatus = 'rejected'
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.sectionsStatus = 'fulfilled'
        state.sections = action.payload
      })
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.classesStatus = 'fulfilled'
        state.classes = action.payload
      })
      .addCase(fetchAccreditations.pending, (state) => {
        state.accreditationsStatus = 'pending'
      })
      .addCase(fetchAccreditations.rejected, (state) => {
        state.accreditationsStatus = 'rejected'
      })
      .addCase(fetchAccreditations.fulfilled, (state, action) => {
        state.accreditationsStatus = 'fulfilled'
        state.accreditations = action.payload
      })
      .addCase(fetchTitleScreens.pending, (state) => {
        state.titleScreensStatus = 'pending'
      })
      .addCase(fetchTitleScreens.rejected, (state) => {
        state.titleScreensStatus = 'rejected'
      })
      .addCase(fetchTitleScreens.fulfilled, (state, action) => {
        state.titleScreensStatus = 'fulfilled'
        state.titleScreens[state.activeClass] = action.payload
      })
      .addCase(fetchVideos.pending, (state) => {
        state.videosStatus = 'pending'
      })
      .addCase(fetchVideos.rejected, (state) => {
        state.videosStatus = 'rejected'
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.videosStatus = 'fulfilled'
        state.videos = action.payload
      })
      .addCase(fetchTextContent.pending, (state) => {
        state.textContentStatus = 'pending'
      })
      .addCase(fetchTextContent.rejected, (state) => {
        state.textContentStatus = 'rejected'
      })
      .addCase(fetchTextContent.fulfilled, (state, action) => {
        state.textContentStatus = 'fulfilled'
        state.textContent[state.activeClass] = action.payload
      })
      .addCase(fetchQuizzes.pending, (state) => {
        state.quizzesStatus = 'pending'
      })
      .addCase(fetchQuizzes.rejected, (state) => {
        state.quizzesStatus = 'rejected'
      })
      .addCase(fetchQuizzes.fulfilled, (state, action) => {
        state.quizzesStatus = 'fulfilled'
        state.quizzes[state.activeClass] = action.payload
      })
      .addCase(fetchStoryboardSteps.pending, (state) => {
        state.storyboardsStatus = 'pending'
      })
      .addCase(fetchStoryboardSteps.rejected, (state) => {
        state.storyboardsStatus = 'rejected'
      })
      .addCase(fetchStoryboardSteps.fulfilled, (state, action) => {
        state.storyboardsStatus = 'fulfilled'
        state.storyboardSteps = action.payload
      })
      .addCase(fetchSeries.pending, (state) => {
        state.seriesStatus = 'pending'
      })
      .addCase(fetchSeries.rejected, (state) => {
        state.seriesStatus = 'rejected'
      })
      .addCase(fetchSeries.fulfilled, (state, action) => {
        state.seriesStatus = 'fulfilled'
        state.series = action.payload
      })
      .addCase(fetchLearningPaths.pending, (state) => {
        state.learningPathsStatus = 'pending'
      })
      .addCase(fetchLearningPaths.rejected, (state) => {
        state.learningPathsStatus = 'rejected'
      })
      .addCase(fetchLearningPaths.fulfilled, (state, action) => {
        state.learningPathsStatus = 'fulfilled'
        state.learningPaths = action.payload
      })
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
  },
})

export let {
  activeLinkedServiceChanged,
  activeSectionChanged,
  activeCourseChanged,
  activeClassChanged,
  sectionsChanged,
  titleScreensChanged,
} = createSlice.actions

export let selectActiveLinkedService = (state: AppRootState) =>
  state.create.activeLinkedService
export let selectActiveSection = (state: AppRootState) =>
  state.create.activeSection
export let selectActiveCourse = (state: AppRootState) =>
  state.create.activeCourse
export let selectActiveClass = (state: AppRootState) => state.create.activeClass

export let selectTemplates = (state: AppRootState) => state.create.templates
export let selectTemplatesStatus = (state: AppRootState) =>
  state.create.templatesStatus
export let selectTemplatesByLinkedServiceID =
  (id: string) => (state: AppRootState) =>
    [
      ...state.create.templates.filter((t) => t.linkedServiceID == null),
      ...state.create.templates.filter((t) => t.linkedServiceID === id),
    ].sort((a, b) => a.name.localeCompare(b.name))

export let selectSections = (state: AppRootState) => state.create.sections
export let selectSectionsStatus = (state: AppRootState) =>
  state.create.sectionsStatus
export let selectSectionByID = (id: string) => (state: AppRootState) =>
  state.create.sections.find((s) => s.id === id)

export let selectCourses = (state: AppRootState) => state.create.courses
export let selectCoursesStatus = (state: AppRootState) =>
  state.create.coursesStatus
export let selectCoursesBySectionID = (id: string) => (state: AppRootState) =>
  state.create.courses
    .filter((c) => c.sectionID === id)
    .sort((a, b) => a.name.localeCompare(b.name))
export let selectCourseByID = (id: string) => (state: AppRootState) =>
  state.create.courses.find((c) => c.id === id)

export let selectClasses = (state: AppRootState) => state.create.classes
export let selectClassesStatus = (state: AppRootState) =>
  state.create.classesStatus
export let selectClassesByCourseID = (id: string) => (state: AppRootState) =>
  state.create.classes
    .filter((c) => c.courseID === id)
    .sort((a, b) => a.sequence - b.sequence)
export let selectClassByID = (id: string) => (state: AppRootState) =>
  state.create.classes.find((c) => c.id === id)

export let selectAccreditations = (state: AppRootState) =>
  state.create.accreditations
export let selectAccreditationsStatus = (state: AppRootState) =>
  state.create.accreditationsStatus
export let selectAccreditationsByCourseID =
  (id: string) => (state: AppRootState) =>
    state.create.accreditations.filter(
      (a) =>
        (a.capce?.courseID ||
          a.ceBroker?.courseID ||
          a.newJersey?.courseID ||
          a.newYork?.courseID ||
          a.virginia?.courseID ||
          a.notAccredited?.courseID) === id,
    )

export let selectTitleScreenByClassID = (id: string) => (state: AppRootState) =>
  state.create.titleScreens[id]
export let selectTitleScreensStatus = (state: AppRootState) =>
  state.create.titleScreensStatus

export let selectVideosByClassID = (id: string) => (state: AppRootState) =>
  state.create.videos.filter((v) => v.classID === id)
export let selectVideoByID = (id: string) => (state: AppRootState) =>
  state.create.videos.find((v) => v.id === id)
export let selectVideosStatus = (state: AppRootState) =>
  state.create.videosStatus

export let selectTextContentByClassID = (id: string) => (state: AppRootState) =>
  state.create.textContent[id]
export let selectTextContentStatus = (state: AppRootState) =>
  state.create.textContentStatus

export let selectSectionsByLinkedService =
  (id: string) => (state: AppRootState) =>
    [
      ...state.create.sections.filter((s) => s.linkedServiceID == null),
      ...state.create.sections.filter((s) => s.linkedServiceID === id),
    ].sort((a, b) => a.name.localeCompare(b.name))

export let selectQuizByClassID = (id: string) => (state: AppRootState) =>
  state.create.quizzes[id]
export let selectQuizzesStatus = (state: AppRootState) =>
  state.create.quizzesStatus

export let selectStoryboardSteps = (state: AppRootState) =>
  state.create.storyboardSteps
export let selectStoryboardStepsByClassID =
  (id: string) => (state: AppRootState) =>
    state.create.storyboardSteps
      .filter((ss) => ss.classID === id)
      .sort((a, b) => a.stepIndex - b.stepIndex)
export let selectStoryboardStepsStatus = (state: AppRootState) =>
  state.create.storyboardsStatus

export let selectSeries = (state: AppRootState) => state.create.series
export let selectSeriesStatus = (state: AppRootState) =>
  state.create.seriesStatus

export let selectLearningPathsByLinkedService =
  (id: string) => (state: AppRootState) =>
    [
      ...state.create.learningPaths.filter((s) => s.linkedServiceID == null),
      ...state.create.learningPaths.filter((s) => s.linkedServiceID === id),
    ].sort((a, b) => a.name.localeCompare(b.name))
export let selectLearningPathsStatus = (state: AppRootState) =>
  state.create.learningPathsStatus

export let selectLinkedServices = (state: AppRootState) =>
  state.create.linkedServices
export let selectLinkedServiceById = (id: string) => (state: AppRootState) =>
  state.create.linkedServices.find((x) => x.id === id)
export let selectLinkedServicesByIds =
  (ids: string[]) => (state: AppRootState) =>
    state.create.linkedServices.filter((x) => ids.includes(x.id ?? ''))
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.create.linkedServicesStatus

export let resetActiveEntityOnChange =
  // eslint-disable-next-line consistent-return
  (store: any) => (next: any) => (action: any) => {
    if (action.type === 'create/activeLinkedServiceChanged') {
      store.dispatch(activeSectionChanged('unselected'))
      store.dispatch(activeCourseChanged('unselected'))
      store.dispatch(activeClassChanged('unselected'))
    }

    if (action.type === 'create/activeSectionChanged') {
      store.dispatch(activeCourseChanged('unselected'))
      store.dispatch(activeClassChanged('unselected'))
    }

    if (action.type === 'create/activeCourseChanged') {
      store.dispatch(activeClassChanged('unselected'))
    }

    if (action) {
      return next(action)
    }
  }

export default createSlice.reducer
